/**
 * OAuth ad-account picker shared by Meta, Google, and Microsoft. The login
 * token stays on the server; the browser receives only the accounts that the
 * signed-in user can choose from.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";

interface AdAccountOption {
  id: string;
  name: string;
  currency?: string;
  detail?: string;
}

export default function AdAccountPicker({
  platform,
  label,
  onClose,
  onConnected,
}: {
  platform: "meta_ads" | "google_ads" | "microsoft_ads";
  label: string;
  onClose: () => void;
  onConnected: () => void;
}): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: [platform, "oauth-ad-accounts"],
    queryFn: () =>
      api.get<{ pending: boolean; accounts: AdAccountOption[] }>(
        `/integrations/${platform}/oauth/ad-accounts`,
      ),
  });

  const connect = useMutation({
    mutationFn: () =>
      api.post(`/integrations/${platform}/oauth/select-account`, {
        adAccountId: selected,
      }),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onConnected();
      onClose();
    },
  });

  const error = connect.error as { detail?: { error?: string } } | null;
  const errorMsg = error?.detail?.error ?? (connect.error ? String(connect.error) : null);
  const expired = data && !data.pending;
  const accounts = data?.accounts ?? [];

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Choose your {label} account</h2>
          <button type="button" aria-label="Close" style={styles.close} onClick={onClose}>
            ×
          </button>
        </div>

        {isLoading && <p style={styles.intro}>Loading the accounts you can access…</p>}
        {isError && (
          <p style={styles.error}>
            We couldn't load your accounts. Close this window and try the {label} connection again.
          </p>
        )}
        {expired && (
          <p style={styles.intro}>
            Your sign-in expired. Close this window, then start the {label} connection again.
          </p>
        )}

        {!isLoading && !isError && !expired && (
          <>
            <p style={styles.intro}>
              Pick the account this business uses. We'll pull its daily ad spend automatically.
            </p>

            {accounts.length === 0 ? (
              <p style={styles.error}>
                That login can't see any ad accounts. Try again with someone who has access to the
                account.
              </p>
            ) : (
              <div style={styles.options}>
                {accounts.map((account) => (
                  <label
                    key={account.id}
                    style={{
                      ...styles.option,
                      ...(selected === account.id ? styles.optionSelected : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name={`${platform}-ad-account`}
                      checked={selected === account.id}
                      onChange={() => setSelected(account.id)}
                    />
                    <span style={{ flex: 1 }}>
                      <span style={{ display: "block", fontSize: 14 }}>{account.name}</span>
                      <span style={styles.accountMeta}>
                        {account.detail ?? account.id}
                        {account.currency ? ` · ${account.currency}` : ""}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            {errorMsg && <p style={styles.error}>{errorMsg}</p>}

            <div style={styles.actions}>
              <button type="button" style={styles.cancel} onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                style={styles.submit}
                disabled={!selected || connect.isPending}
                onClick={() => connect.mutate()}
              >
                {connect.isPending ? "Connecting…" : "Connect this account"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 24,
    width: 480,
    maxWidth: "95vw",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  close: {
    background: "transparent",
    color: "var(--body)",
    border: "none",
    fontSize: 24,
    cursor: "pointer",
    padding: 0,
    width: 28,
    height: 28,
  },
  intro: {
    color: "var(--body)",
    fontSize: 13,
    marginTop: 0,
    marginBottom: 16,
    lineHeight: 1.5,
  },
  options: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: 320,
    overflowY: "auto",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    border: "1px solid var(--border)",
    borderRadius: 8,
    cursor: "pointer",
  },
  optionSelected: {
    borderColor: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 8%, transparent)",
  },
  accountMeta: {
    display: "block",
    fontSize: 12,
    color: "var(--body)",
  },
  error: {
    color: "var(--bad)",
    fontSize: 13,
    margin: "12px 0 0",
    padding: "8px 12px",
    background: "color-mix(in srgb, var(--bad) 14%, var(--surface))",
    border: "1px solid color-mix(in srgb, var(--bad) 35%, var(--border))",
    borderRadius: 6,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
  },
  cancel: {
    background: "transparent",
    color: "var(--body)",
    border: "1px solid var(--border)",
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer",
  },
  submit: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    padding: "8px 20px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
};
